/**
 * useAssessmentGeneration Hook
 * ORCA360 - Assessment Generation Integration
 * 
 * React hook for triggering AI-powered assessment generation
 * Provides progress tracking, error handling, and status updates
 * 
 * Sprint 3, Step 2, Phase 3
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
// Note: useValidationJob import removed - both pilot and instructor now use direct validation endpoints
// Note: buildDisplayNameMap import removed - backend now handles transcript retrieval (aligned with mobile pipeline)

/**
 * Evidence item structure with validation metadata
 */
export interface EvidenceItem {
  quote: string;
  timestamp: string;
  type: 'positive' | 'deficiency';
  context: string;
  validation_status: string | null;
  validation_tier?: string | null;
  validation_score?: number | null;
}

/**
 * Assessment generation response
 */
export interface AssessmentGenerationResult {
  success: boolean;
  sessionId: string;
  traineeId: string;
  instructorId: string;
  assessments: Array<{
    id: string;
    competency: string;
    score: number | null;
    evidence: EvidenceItem[];
    analysis: string | null;
    goals: string | null;
    facilitator_questions: string[] | null;
  }>;
  instructorAssessments: Array<{
    id: string;
    criterion: string;
    score: number | null;
    evidence: EvidenceItem[];
    analysis: string | null;
    goals: string | null;
  }>;
  templateSources?: Record<string, 'org-specific' | 'global' | 'fallback'>;
  templateWarnings?: string[];
  metadata: {
    spanBasedExtraction?: boolean; // If true, evidence is verbatim - skip validation
    evidenceCount: number;
    competenciesAssessed: number;
    storedCount: number;
    failedCount: number;
    instructorCriteriaAssessed: number;
    instructorStoredCount: number;
    instructorFailedCount: number;
    processingTime: {
      evidenceExtraction: number;
      sopRetrieval: number;
      assessmentGeneration: number;
      rcaGeneration: number;
      smartGoalsGeneration: number;
      pilotAssessmentGeneration: number;
      instructorAssessmentGeneration: number;
      databaseStorage: number;
      evidenceValidation: number;
      total: number;
    };
    rcaStats: {
      enabled: boolean;
      threshold: number;
      generated: number;
    };
    smartGoalsStats: {
      enabled: boolean;
      threshold: number;
      generated: number;
    };
    avgScore: number;
    instructorAvgScore: number;
    instructorValidationPending?: boolean;
    validationStats: {
      total: number;
      verified: number;
      paraphrased: number;
      similar: number;
      unverified: number;
      verificationRate: number;
      avgConfidence: number;
    } | null;
    instructorValidationStats: {
      total: number;
      verified: number;
      paraphrased: number;
      similar: number;
      unverified: number;
      verificationRate: number;
      avgConfidence: number;
    } | null;
  };
}

/**
 * Assessment generation error
 */
export interface AssessmentGenerationError {
  error: string;
  message: string;
  details?: string | Record<string, unknown>;
}

/**
 * Progress stages
 */
export enum GenerationStage {
  IDLE = 'idle',
  FETCHING_TRANSCRIPT = 'fetching_transcript',
  EXTRACTING_EVIDENCE = 'extracting_evidence',
  GENERATING_ASSESSMENTS = 'generating_assessments',
  STORING_DATABASE = 'storing_database',
  VALIDATING_EVIDENCE = 'validating_evidence',
  COMPLETE = 'complete',
  ERROR = 'error',
}

/**
 * Validation status enum
 */
export type ValidationStatus = 'pending' | 'in_progress' | 'completed' | 'failed' | null;

/**
 * Polling interval for validation status checks (milliseconds)
 */
const VALIDATION_POLLING_INTERVAL_MS = 5000;

/**
 * Assessment type parameter - determines which assessments to generate
 */
export type AssessmentType = 'pilot' | 'instructor';

/**
 * Hook return type
 */
export interface UseAssessmentGeneration {
  generateAssessments: (sessionId: string, assessmentType?: AssessmentType) => Promise<AssessmentGenerationResult>;
  isGenerating: boolean;
  progress: number; // 0-100
  stage: GenerationStage;
  error: string | null;
  result: AssessmentGenerationResult | null;
  validationStatus: ValidationStatus;
  reset: () => void;
  // Note: Validation job tracking removed - both pilot and instructor now use direct endpoints
}

/**
 * Hook implementation
 */
export function useAssessmentGeneration(): UseAssessmentGeneration {
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState<GenerationStage>(GenerationStage.IDLE);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AssessmentGenerationResult | null>(null);

  // Validation status tracking (Sprint 5)
  // Note: Both pilot and instructor validation now use direct endpoints instead of queue-based system
  const [validationStatus, setValidationStatus] = useState<ValidationStatus>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  // Safety-net: if validation_status never transitions from in_progress (e.g. endpoint failure),
  // this timeout ensures the UI eventually escapes the polling loop.
  const validationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Reset hook state
   */
  const reset = useCallback(() => {
    setIsGenerating(false);
    setProgress(0);
    setStage(GenerationStage.IDLE);
    setError(null);
    setResult(null);
    setValidationStatus(null);

    // Clear polling interval and safety-net timeout if active
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
      validationTimeoutRef.current = null;
    }
  }, []);

  /**
   * Main function to generate assessments
   * Note: supabase client is a stable singleton and doesn't need to be in dependencies
   * @param sessionId - The session ID to generate assessments for
   * @param assessmentType - 'pilot' or 'instructor' - controls which assessment to generate
   */
  const generateAssessments = useCallback(
    async (sessionId: string, assessmentType: AssessmentType = 'pilot'): Promise<AssessmentGenerationResult> => {
      console.log(`[useAssessmentGeneration] Starting ${assessmentType} assessment generation...`);
      // Reset state
      setIsGenerating(true);
      setProgress(0);
      setError(null);
      setResult(null);

      try {
        // Stage 1: Prepare for assessment generation
        // Backend handles transcript retrieval from session_transcripts or speaker_segments
        // (aligned with mobile pipeline — no client-side transcript reconstruction)
        setStage(GenerationStage.FETCHING_TRANSCRIPT);
        setProgress(10);

        console.log(`[useAssessmentGeneration] Triggering ${assessmentType} assessment for session: ${sessionId}`);

        setProgress(20);

        // Stage 2: Call assessment generation API
        setStage(GenerationStage.EXTRACTING_EVIDENCE);
        console.log('[useAssessmentGeneration] Calling assessment API...');

        // Progress is SIMULATED (not real-time API progress).
        // The API can take 60-300 seconds depending on transcript length and OpenAI response times.
        const progressSimulation = (async () => {
          // Evidence extraction (20% → 40%)
          await new Promise((resolve) => setTimeout(resolve, 2000));
          setProgress(30);
          await new Promise((resolve) => setTimeout(resolve, 3000));
          setProgress(40);

          // Assessment generation (40% → 60%)
          setStage(GenerationStage.GENERATING_ASSESSMENTS);
          await new Promise((resolve) => setTimeout(resolve, 5000));
          setProgress(50);
          await new Promise((resolve) => setTimeout(resolve, 5000));
          setProgress(60);

          // Database storage (60% → 75%)
          setStage(GenerationStage.STORING_DATABASE);
          await new Promise((resolve) => setTimeout(resolve, 3000));
          setProgress(70);

          // Evidence validation (75% → 90%)
          setStage(GenerationStage.VALIDATING_EVIDENCE);
          await new Promise((resolve) => setTimeout(resolve, 3000));
          setProgress(80);
          await new Promise((resolve) => setTimeout(resolve, 3000));
          setProgress(90);
        })();

        // Get auth token for authorization
        const { data: { session: authSession } } = await supabase.auth.getSession();
        const authToken = authSession?.access_token;
        if (!authToken) {
          throw new Error('Not authenticated. Please log in and try again.');
        }

        const response = await fetch('/api/generate-assessment', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            sessionId,
            assessmentType,
          }),
        });

        // Wait for progress simulation to catch up (in case API was very fast)
        await progressSimulation;

        // Check response
        if (!response.ok) {
          let errorMessage = 'Assessment generation failed';
          try {
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              const errorData: AssessmentGenerationError = await response.json();
              errorMessage = errorData.message || errorMessage;
            } else {
              // Response is not JSON (likely HTML error page or plain text)
              const textError = await response.text();
              errorMessage = `API Error (${response.status}): ${textError.substring(0, 200)}`;
              console.error('[useAssessmentGeneration] Non-JSON error response:', textError);
            }
          } catch (parseError) {
            console.error('[useAssessmentGeneration] Error parsing error response:', parseError);
            errorMessage = `API Error (${response.status}): Could not parse error response`;
          }
          throw new Error(errorMessage);
        }

        const generationResult: AssessmentGenerationResult = await response.json();

        console.log('[useAssessmentGeneration] Generation complete:', {
          pilotAssessments: generationResult.assessments.length,
          instructorAssessments: generationResult.instructorAssessments.length,
          avgScore: generationResult.metadata.avgScore,
          instructorAvgScore: generationResult.metadata.instructorAvgScore,
          verificationRate: generationResult.metadata.validationStats?.verificationRate,
        });

        // Check if span-based extraction was used (evidence is verbatim - skip validation)
        const spanBasedExtraction = generationResult.metadata.spanBasedExtraction === true;

        // Check if validation completed inline (both pilot and instructor can complete inline now)
        const pilotInlineDone = generationResult.metadata.validationStats !== null;
        const instructorInlineDone = generationResult.metadata.instructorValidationStats !== null;
        const hasInstructorAssessments = (generationResult.instructorAssessments?.length || 0) > 0;
        const instructorValidationPending = generationResult.metadata.instructorValidationPending === true;

        // Skip validation entirely if span-based extraction was used
        // (evidence quotes are verbatim by construction - validation would be redundant)
        if (spanBasedExtraction) {
          console.log('[useAssessmentGeneration] Span-based extraction used - evidence is verbatim, skipping validation');
          setValidationStatus('completed');
          setStage(GenerationStage.COMPLETE);
          setProgress(100);
          setResult(generationResult);
          setIsGenerating(false);
        } else if (pilotInlineDone && (!hasInstructorAssessments || instructorInlineDone)) {
          // Both pilot and instructor validation completed inline - no background jobs needed
          console.log('[useAssessmentGeneration] All validation completed inline; no background jobs needed');
          console.log(`[useAssessmentGeneration] Pilot: inline=${pilotInlineDone}, Instructor: inline=${instructorInlineDone}, hasInstructor=${hasInstructorAssessments}`);
          setValidationStatus('completed');
          setStage(GenerationStage.COMPLETE);
          setProgress(100);
          setResult(generationResult);
          setIsGenerating(false);
        } else {
          // Background validation needed - use queue-based system
          setStage(GenerationStage.VALIDATING_EVIDENCE);
          setProgress(90);
          console.log('[useAssessmentGeneration] Triggering queue-based evidence validation...');
          console.log(`[useAssessmentGeneration] Pilot: inline=${pilotInlineDone}, Instructor: inline=${instructorInlineDone}, pending=${instructorValidationPending}`);

          try {
            const pilotEvidenceCount = (generationResult.assessments || []).reduce(
              (sum, assessment) => sum + (assessment.evidence?.length || 0),
              0
            );
            const instructorEvidenceCount = (generationResult.instructorAssessments || []).reduce(
              (sum, assessment) => sum + (assessment.evidence?.length || 0),
              0
            );

            const shouldCreatePilotJob = pilotEvidenceCount > 0 && !pilotInlineDone;
            const shouldCreateInstructorJob = instructorEvidenceCount > 0 && !instructorInlineDone;

            if (!shouldCreatePilotJob && !shouldCreateInstructorJob) {
              console.warn('[useAssessmentGeneration] No evidence items found; skipping validation jobs.');
              setValidationStatus('completed');
            } else {
              // Set status to in_progress immediately
              setValidationStatus('in_progress');

              // Pilot validation - use direct endpoint (fast: ~9 API calls vs ~50 with queue-based)
              if (shouldCreatePilotJob) {
                console.log('[useAssessmentGeneration] Starting pilot validation (direct endpoint)...');

                const authSession = await supabase.auth.getSession();
                const token = authSession.data.session?.access_token;

                if (token) {
                  try {
                    const response = await fetch('/api/validate-evidence', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                      },
                      body: JSON.stringify({ sessionId }),
                    });

                    if (response.ok) {
                      const validationResult = await response.json();
                      console.log('[useAssessmentGeneration] Pilot validation completed:', validationResult);
                    } else {
                      console.warn('[useAssessmentGeneration] Pilot validation failed:', await response.text());
                    }
                  } catch (err) {
                    console.error('[useAssessmentGeneration] Pilot validation error:', err);
                  }
                }
              } else {
                console.warn('[useAssessmentGeneration] Skipping pilot validation (no pilot evidence).');
              }

              // Instructor validation - use direct endpoint (fast: ~5 API calls vs ~25 with queue-based)
              if (shouldCreateInstructorJob) {
                console.log('[useAssessmentGeneration] Starting instructor validation (direct endpoint)...');

                // Reuse token from pilot validation or get fresh one
                const instrAuthSession = await supabase.auth.getSession();
                const instrToken = instrAuthSession.data.session?.access_token;

                if (instrToken) {
                  try {
                    const instrResponse = await fetch('/api/validate-instructor-evidence', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${instrToken}`,
                      },
                      body: JSON.stringify({ sessionId }),
                    });

                    if (instrResponse.ok) {
                      const instrValidationResult = await instrResponse.json();
                      console.log('[useAssessmentGeneration] Instructor validation completed:', instrValidationResult);
                    } else {
                      console.warn('[useAssessmentGeneration] Instructor validation failed:', await instrResponse.text());
                    }
                  } catch (err) {
                    console.error('[useAssessmentGeneration] Instructor validation error:', err);
                  }
                }
              } else if (generationResult.instructorAssessments?.length > 0) {
                console.warn('[useAssessmentGeneration] Skipping instructor validation (no instructor evidence).');
              }

              console.log('[useAssessmentGeneration] ✅ Validation completed via direct endpoints');
            }
          } catch (validationTriggerError: any) {
            console.error('[useAssessmentGeneration] Failed to trigger validation:', validationTriggerError);
            setValidationStatus('failed');
            setError(validationTriggerError.message || 'Failed to create validation jobs');
          }

          // Complete generation - validation continues in background
          setStage(GenerationStage.COMPLETE);
          setProgress(100);
          setResult(generationResult);
          setIsGenerating(false);
        }

        // RCA/SMART now triggered server-side by generate-assessment API
        return generationResult;
      } catch (err) {
        console.error('[useAssessmentGeneration] Error:', err);
        const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';

        setError(errorMessage);
        setStage(GenerationStage.ERROR);
        setIsGenerating(false);

        throw err;
      }
    },
    []
  );

  // Note: Both pilot and instructor validation useEffects removed - both now use direct endpoints and complete synchronously

  /**
   * Poll for validation status when background validation is in progress.
   * Includes a consecutive-error counter and a 10-minute safety-net timeout to ensure
   * the UI never gets permanently stuck in 'in_progress' if the background job fails
   * silently (e.g. validation endpoint returned an error and never updated the DB).
   */
  useEffect(() => {
    // Only poll if we have a result and validation is in progress
    if (!result?.sessionId || validationStatus !== 'in_progress') {
      return;
    }

    console.log('[useAssessmentGeneration] Starting validation status polling...');

    let consecutiveErrors = 0;
    const MAX_CONSECUTIVE_ERRORS = 3;

    // Safety-net: if validation_status in DB never transitions (endpoint failure),
    // give up after 10 minutes so the UI doesn't poll forever.
    const timeoutId = setTimeout(() => {
      console.warn('[useAssessmentGeneration] ⏰ Validation timed out after 10 minutes - marking failed');
      setValidationStatus('failed');
    }, 10 * 60 * 1000);
    validationTimeoutRef.current = timeoutId;

    // Poll every 5 seconds
    const pollInterval = setInterval(async () => {
      try {
        const { data: session, error: sessionError } = await supabase
          .from('sessions')
          .select('validation_status, validation_error')
          .eq('id', result.sessionId)
          .single();

        if (sessionError) {
          consecutiveErrors++;
          console.error('[useAssessmentGeneration] Polling error:', sessionError);
          if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
            console.error('[useAssessmentGeneration] Too many consecutive DB errors - marking validation failed');
            setValidationStatus('failed');
          }
          return;
        }

        consecutiveErrors = 0; // Reset on successful DB read

        if (session?.validation_status === 'completed') {
          console.log('[useAssessmentGeneration] ✅ Background validation completed!');
          setValidationStatus('completed');
        } else if (session?.validation_status === 'failed') {
          console.error('[useAssessmentGeneration] ❌ Background validation failed:', session.validation_error);
          setValidationStatus('failed');
        } else {
          console.log('[useAssessmentGeneration] Validation still in progress...');
        }
      } catch (pollError) {
        consecutiveErrors++;
        console.error('[useAssessmentGeneration] Polling exception:', pollError);
        if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
          console.error('[useAssessmentGeneration] Too many consecutive polling exceptions - marking validation failed');
          setValidationStatus('failed');
        }
      }
    }, VALIDATION_POLLING_INTERVAL_MS);

    pollingIntervalRef.current = pollInterval;

    // Cleanup on unmount or when validation status changes
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
        validationTimeoutRef.current = null;
      }
    };
  }, [result?.sessionId, validationStatus]);

  return {
    generateAssessments,
    isGenerating,
    progress,
    stage,
    error,
    result,
    validationStatus,
    reset,
    // Note: Validation job tracking removed - both pilot and instructor now use direct endpoints
  };
}

/**
 * Get user-friendly stage label
 */
export function getStageLabel(stage: GenerationStage): string {
  switch (stage) {
    case GenerationStage.IDLE:
      return 'Ready';
    case GenerationStage.FETCHING_TRANSCRIPT:
      return 'Loading transcript...';
    case GenerationStage.EXTRACTING_EVIDENCE:
      return 'Extracting evidence from transcript...';
    case GenerationStage.GENERATING_ASSESSMENTS:
      return 'Generating competency assessments...';
    case GenerationStage.STORING_DATABASE:
      return 'Storing assessments...';
    case GenerationStage.VALIDATING_EVIDENCE:
      return 'Validating evidence (anti-hallucination)...';
    case GenerationStage.COMPLETE:
      return 'Complete!';
    case GenerationStage.ERROR:
      return 'Error';
    default:
      return 'Processing...';
  }
}

/**
 * Get validation status badge color
 */
export function getValidationColor(status: string): string {
  switch (status) {
    case 'VERIFIED':
      return 'green';
    case 'PARAPHRASED':
      return 'yellow';
    case 'SIMILAR':
      return 'orange';
    case 'UNVERIFIED':
      return 'red';
    default:
      return 'gray';
  }
}

/**
 * Get validation status icon
 */
export function getValidationIcon(status: string): string {
  switch (status) {
    case 'VERIFIED':
      return '✅';
    case 'PARAPHRASED':
      return '⚠️';
    case 'SIMILAR':
      return '⚠️';
    case 'UNVERIFIED':
      return '❌';
    default:
      return '❓';
  }
}

